import React, { useCallback, useMemo } from 'react';
import {
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Repeat,
  Circle,
} from 'lucide-react';
import { useAnimationStore } from '../../store/animationStore';
import { useAnimationPlayback } from '../../hooks/useAnimationPlayback';
import { cn } from '../../utils/cn';

interface PlaybackControlsProps {
  className?: string;
}

const FPS_OPTIONS = [12, 24, 30, 60, 120];

export const PlaybackControls: React.FC<PlaybackControlsProps> = ({ className }) => {
  const {
    selectedAnimationId,
    animations,
    playback,
    isRecording,
    play,
    pause,
    stop,
    goToStart,
    goToEnd,
    stepForward,
    stepBackward,
    setFps,
    updateAnimation,
    setRecording,
  } = useAnimationStore();

  // Use playback hook for animation loop handling
  useAnimationPlayback();

  const animation = selectedAnimationId ? animations[selectedAnimationId] : null;
  const { isPlaying, currentTime, currentFrame, fps } = playback;
  const duration = animation?.duration || 1;
  const totalFrames = Math.round(duration * fps);

  // Toggle play/pause
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Stop and reset to start
  const handleStop = useCallback(() => {
    stop();
  }, [stop]);

  // Toggle loop
  const handleToggleLoop = useCallback(() => {
    if (animation) {
      updateAnimation(animation.id, { loop: !animation.loop });
    }
  }, [animation, updateAnimation]);

  // Toggle recording mode
  const handleToggleRecording = useCallback(() => {
    setRecording(!isRecording);
  }, [isRecording, setRecording]);

  // Handle FPS change
  const handleFpsChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newFps = parseInt(e.target.value, 10);
      setFps(newFps);
    },
    [setFps]
  );

  // Format time display
  const formatTime = useMemo(() => {
    const mins = Math.floor(currentTime / 60);
    const secs = currentTime % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toFixed(2).padStart(5, '0')}`;
  }, [currentTime]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if we're in an input field or typing in an editor
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true' ||
        activeElement?.closest('.monaco-editor') ||
        activeElement?.closest('[data-script-editor]')
      ) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'ArrowLeft':
          if (e.shiftKey) {
            goToStart();
          } else {
            stepBackward();
          }
          break;
        case 'ArrowRight':
          if (e.shiftKey) {
            goToEnd();
          } else {
            stepForward();
          }
          break;
        case 'Home':
          goToStart();
          break;
        case 'End':
          goToEnd();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handlePlayPause, goToStart, goToEnd, stepForward, stepBackward]);

  const isDisabled = !animation;

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-2 bg-zinc-800 border-b border-zinc-700',
        className
      )}
    >
      {/* Transport controls */}
      <div className="flex items-center gap-1">
        {/* Go to start */}
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={goToStart}
          disabled={isDisabled}
          title="Go to Start (Home)"
        >
          <SkipBack size={16} />
        </button>

        {/* Step backward */}
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={stepBackward}
          disabled={isDisabled}
          title="Previous Frame (Left Arrow)"
        >
          <ChevronLeft size={16} />
        </button>

        {/* Play/Pause */}
        <button
          className={cn(
            'p-2 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : isPlaying
              ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
              : 'text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={handlePlayPause}
          disabled={isDisabled}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
        </button>

        {/* Stop */}
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={handleStop}
          disabled={isDisabled}
          title="Stop"
        >
          <Square size={16} />
        </button>

        {/* Step forward */}
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={stepForward}
          disabled={isDisabled}
          title="Next Frame (Right Arrow)"
        >
          <ChevronRight size={16} />
        </button>

        {/* Go to end */}
        <button
          className={cn(
            'p-1.5 rounded transition-colors',
            isDisabled
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
          )}
          onClick={goToEnd}
          disabled={isDisabled}
          title="Go to End (End)"
        >
          <SkipForward size={16} />
        </button>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-zinc-700" />

      {/* Time/Frame display */}
      <div className="flex items-center gap-2 text-sm font-mono">
        <span className="text-zinc-300 min-w-[70px]">{formatTime}</span>
        <span className="text-zinc-500">|</span>
        <span className="text-zinc-400 min-w-[80px]">
          {currentFrame} / {totalFrames}
        </span>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-zinc-700" />

      {/* Loop toggle */}
      <button
        className={cn(
          'p-1.5 rounded transition-colors',
          isDisabled
            ? 'text-zinc-600 cursor-not-allowed'
            : animation?.loop
            ? 'text-blue-400 bg-blue-500/20 hover:bg-blue-500/30'
            : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
        )}
        onClick={handleToggleLoop}
        disabled={isDisabled}
        title={animation?.loop ? 'Loop: On' : 'Loop: Off'}
      >
        <Repeat size={16} />
      </button>

      {/* Record toggle */}
      <button
        className={cn(
          'p-1.5 rounded transition-colors',
          isDisabled
            ? 'text-zinc-600 cursor-not-allowed'
            : isRecording
            ? 'text-red-400 bg-red-500/20 hover:bg-red-500/30'
            : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700'
        )}
        onClick={handleToggleRecording}
        disabled={isDisabled}
        title={isRecording ? 'Recording: On' : 'Recording: Off'}
      >
        <Circle size={16} fill={isRecording ? 'currentColor' : 'none'} />
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* FPS selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">FPS:</span>
        <select
          className={cn(
            'bg-zinc-700 text-zinc-300 text-sm px-2 py-1 rounded border border-zinc-600',
            'focus:outline-none focus:border-blue-500',
            isDisabled && 'opacity-50 cursor-not-allowed'
          )}
          value={fps}
          onChange={handleFpsChange}
          disabled={isDisabled}
        >
          {FPS_OPTIONS.map((fpsOption) => (
            <option key={fpsOption} value={fpsOption}>
              {fpsOption}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
